import { ArrowLeft01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable } from 'react-native';

import { useScreenOptions } from './ScreenOptionsContext';
import { Box } from '../gluestack/box';
import { HStack } from '../gluestack/hstack';
import { View } from '../gluestack/view';
import { VStack } from '../gluestack/vstack';

import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';

export function LargeScreenTitleBar() {
  const { currentOptions } = useScreenOptions();
  const { t } = useTranslation();
  const router = useRouter();
  const hasBackgroundContent = !!currentOptions.headerBackgroundContent;
  // True for cover-image headers (profiles/events). Prefer the explicit flag so
  // the back-button style is stable from first render; fall back to the cover's
  // presence. headerBackgroundContent alone arrives late (after the image
  // loads), which would otherwise flip the chevron from plain to blur pill.
  const onImageHeader = currentOptions.backButtonOnImage || hasBackgroundContent;
  // Web keeps the browser back button. On native large screens, subpages
  // (profiles, settings, single post, ...) get a back chevron before the title;
  // list roots reachable from the left panel opt out via hideBackButton.
  // canGoBack() guards against a dead button (e.g. a deep-linked cold start).
  // Visibility re-evaluates whenever a screen registers its options on focus.
  const showBackButton =
    Platform.OS !== 'web' && !currentOptions.hideBackButton && router.canGoBack();

  if (currentOptions.headerShown === false) {
    return null;
  }

  // Over a profile/event cover image, use MobileHeader's transparent-header
  // treatment (dark blur pill + white icon, top-aligned) for contrast; a plain
  // brand-color chevron centered on the title everywhere else.
  const backChevron = !showBackButton ? null : onImageHeader ? (
    <Box className="self-start overflow-hidden rounded-full">
      <BlurView intensity={80} tint="dark" className="p-1">
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityLabel={t('common.back')}>
          <HugeIconsWrapper icon={ArrowLeft01Icon} size={28} className="color-white" />
        </Pressable>
      </BlurView>
    </Box>
  ) : (
    <Pressable onPress={() => router.back()} hitSlop={10} accessibilityLabel={t('common.back')}>
      <HugeIconsWrapper icon={ArrowLeft01Icon} size={28} className="text-primary-500" />
    </Pressable>
  );

  return (
    <View className="relative overflow-hidden">
      {currentOptions.headerBackgroundContent}
      
      <VStack className="bg-transparent">
        <HStack className="mx-5 justify-between py-3">
          {/* On a profile/event header the title is null, so this gap is the
              one between the back button and the photo — match the photo→content
              gap (gap-5) there; keep the tighter md gap for plain title pages. */}
          <HStack space={onImageHeader ? 'xl' : 'md'} className="flex-1 items-center pt-1">
            {/* List roots opt out via hideBackButton; other subpages (incl.
                profiles) get the chevron. */}
            {backChevron}
            {(() => {
              if (typeof currentOptions.headerTitle === 'function') {
                return currentOptions.headerTitle({ children: currentOptions.title ?? '', tintColor: undefined });
              }
              const titleText = typeof currentOptions.headerTitle === 'string' 
                ? currentOptions.headerTitle 
                : currentOptions.title;
              
              return titleText ? (
                <Text size="2xl" bold className="text-primary-500">
                  {titleText}
                </Text>
              ) : null;
            })()}
            {currentOptions.headerLeft && (
              <HStack className={hasBackgroundContent ? "flex-1" : "mr-4"}>
                {currentOptions.headerLeft({})}
              </HStack>
            )}
          </HStack>
          {currentOptions.headerRight && (
            <HStack space="md" className="items-start">
              {currentOptions.headerRight({})}
            </HStack>
          )}
        </HStack>

        {currentOptions.inlineTabsBar && (
          <View className="w-full">
            {currentOptions.inlineTabsBar}
          </View>
        )}
      </VStack>
    </View>
  );
}
